import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { categoryMeta } from "@/mocks/videos";

const contacts = [
  {
    icon: "ri-mail-line",
    label: "客服邮箱",
    value: "service@99video.example.com",
    href: "mailto:service@99video.example.com",
  },
  {
    icon: "ri-wechat-line",
    label: "官方微信",
    value: "video99",
  },
  {
    icon: "ri-weibo-line",
    label: "官方微博",
    value: "@99视频",
  },
  {
    icon: "ri-time-line",
    label: "服务时间",
    value: "每日 9:00 - 22:00",
  },
];

export default function AboutPage() {
  useDocumentTitle(
    "关于我们 - 99视频高清视频平台",
    "了解99视频的网站定位、内容涵盖类型与联系方式。99视频提供电影、电视剧、综艺等高清影视内容的发现与浏览服务。"
  );

  return (
    <div className="w-full">
      <section className="w-full bg-primary-900">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="[&_a]:text-background-200/80 [&_span]:text-background-50">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          </div>
          <h1 className="mt-5 text-3xl md:text-5xl font-extrabold text-background-50">
            关于 99视频
          </h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-background-200/90 leading-relaxed">
            一个为影视爱好者打造的高清内容发现平台，让查找与浏览影视资源变得简单而愉快。
          </p>
        </div>
      </section>

      <section className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-foreground-950">
              网站定位
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
              99视频致力于为广大影视爱好者提供一个海量高清视频资源观看与发现的平台。我们希望每一位用户都能通过清晰的分类、灵活的筛选与贴心的推荐，快速定位到想看的内容，享受纯粹、舒适且高效的内容浏览体验。
            </p>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              平台汇聚电影、电视剧、综艺等多种类型的影视内容。无论你是想追一部热播剧集，还是寻找一部经典佳作，亦或是在闲暇时看一档轻松的综艺，都能在 99视频找到合适的选择。
            </p>
          </div>
          <div className="rounded-lg bg-background-100 border border-background-200 p-6">
            <h3 className="text-base font-bold text-foreground-950 mb-4">
              平台特色
            </h3>
            <ul className="space-y-3">
              {[
                "海量高清影视内容",
                "分类清晰、筛选灵活",
                "热点推荐与专题策划",
                "简洁舒适的浏览体验",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5"></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 border-y border-background-200">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
          <h2 className="text-2xl font-bold text-foreground-950">
            内容涵盖类型
          </h2>
          <p className="mt-3 text-sm text-foreground-600">
            三大内容频道，覆盖不同观影口味
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categoryMeta.map((cat) => (
              <div
                key={cat.key}
                className="rounded-lg bg-background-50 border border-background-200 p-6"
              >
                <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center text-2xl">
                  <i className={cat.icon}></i>
                </span>
                <h3 className="mt-4 text-lg font-bold text-foreground-950">
                  {cat.name}
                </h3>
                <p className="mt-2 text-sm font-medium text-accent-700">
                  {cat.tagline}
                </p>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  {cat.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
        <h2 className="text-2xl font-bold text-foreground-950">
          联系我们
        </h2>
        <p className="mt-3 text-sm text-foreground-600">
          如对内容或功能有任何疑问，欢迎通过以下方式与我们取得联系
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {contacts.map((c) => (
            <div
              key={c.label}
              className="rounded-lg bg-background-100 border border-background-200 p-5"
            >
              <span className="w-10 h-10 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center text-xl">
                <i className={c.icon}></i>
              </span>
              <p className="mt-3 text-xs text-foreground-500">{c.label}</p>
              {c.href ? (
                <a
                  href={c.href}
                  className="mt-1 inline-block text-sm font-medium text-primary-700 hover:text-primary-500 transition-colors cursor-pointer break-all"
                >
                  {c.value}
                </a>
              ) : (
                <p className="mt-1 text-sm font-medium text-foreground-900">
                  {c.value}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="w-full bg-background-100 border-t border-background-200">
        <div className="w-full max-w-page mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-foreground-950">
                反馈建议
              </h2>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                我们非常重视每一位用户的意见。无论是内容建议、资源报错还是功能反馈，都欢迎告诉我们，帮助 99视频变得更好。
              </p>
              <div className="mt-6 rounded-lg bg-background-50 border border-background-200 p-5">
                <div className="flex items-start gap-2.5">
                  <i className="ri-information-line text-primary-600 mt-0.5"></i>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    提示：本站为影视内容演示平台，页面内的影视名称、封面与资料均为虚构演示内容，播放功能为前端演示，未接入真实播放接口。
                  </p>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <FeedbackForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}